import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from './client';

export type ActivitySettings = Schemas['ActivitySettingsDto'];
export type LocationPoint = Schemas['LocationDto'];

const key = (ws: string) => ['workspace', ws, 'activity'] as const;
const wsPath = (ws: string) => ({ params: { path: { workspaceId: ws } } });

export function useActivitySettings(ws: string, enabled = true) {
  return useQuery({
    queryKey: [...key(ws), 'settings'],
    queryFn: () => unwrap(api.GET('/api/v1/workspaces/{workspaceId}/activity/settings', wsPath(ws))),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

export function useSaveActivitySettings(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas['ActivitySettingsRequest']) =>
      unwrap(api.PUT('/api/v1/workspaces/{workspaceId}/activity/settings', { ...wsPath(ws), body })),
    onSuccess: (data) => qc.setQueryData([...key(ws), 'settings'], data),
  });
}

export function useSetActivityConsent(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (gps: boolean) => unwrap(api.PUT('/api/v1/workspaces/{workspaceId}/activity/consent', { ...wsPath(ws), body: { gps } })),
    onSuccess: (data) => qc.setQueryData([...key(ws), 'settings'], data),
  });
}

export function useLocations(ws: string, userId: string | null, from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: [...key(ws), 'locations', userId, from, to],
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/workspaces/{workspaceId}/activity/locations', {
          params: { path: { workspaceId: ws }, query: { userId: userId ?? undefined, from, to } },
        }),
      ),
    enabled: enabled && !!userId,
  });
}

export async function sendLocations(ws: string, points: Schemas['Point'][]) {
  return unwrap(api.POST('/api/v1/workspaces/{workspaceId}/activity/locations', { ...wsPath(ws), body: { points } }));
}

export type DesktopSettings = Schemas['DesktopSettingsDto'];
export type Screenshot = Schemas['ScreenshotDto'];
export type AppUsage = Schemas['UsageDto'];

export function useDesktopSettings(ws: string, enabled = true) {
  return useQuery({
    queryKey: [...key(ws), 'desktop'],
    queryFn: () => unwrap(api.GET('/api/v1/workspaces/{workspaceId}/activity/desktop-settings', wsPath(ws))),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

export function useSaveDesktopSettings(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas['DesktopSettingsRequest']) =>
      unwrap(api.PUT('/api/v1/workspaces/{workspaceId}/activity/desktop-settings', { ...wsPath(ws), body })),
    onSuccess: (data) => qc.setQueryData([...key(ws), 'desktop'], data),
  });
}

export function useSetDesktopConsent(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas['DesktopConsentRequest']) =>
      unwrap(api.PUT('/api/v1/workspaces/{workspaceId}/activity/desktop-consent', { ...wsPath(ws), body })),
    onSuccess: (data) => qc.setQueryData([...key(ws), 'desktop'], data),
  });
}

export function useScreenshots(ws: string, userId: string | null, from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: [...key(ws), 'screenshots', userId, from, to],
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/workspaces/{workspaceId}/activity/screenshots', {
          params: { path: { workspaceId: ws }, query: { userId: userId ?? undefined, from, to } },
        }),
      ),
    enabled: enabled && !!userId,
  });
}

export function useDeleteScreenshot(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/v1/workspaces/{workspaceId}/activity/screenshots/{id}', { params: { path: { workspaceId: ws, id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...key(ws), 'screenshots'] }),
  });
}

export function screenshotUrl(ws: string, id: string) {
  return `/api/v1/workspaces/${ws}/activity/screenshots/${id}/image`;
}

export function useAppUsage(ws: string, userId: string | null, from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: [...key(ws), 'app-usage', userId, from, to],
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/workspaces/{workspaceId}/activity/app-usage', {
          params: { path: { workspaceId: ws }, query: { userId: userId ?? undefined, from, to } },
        }),
      ),
    enabled: enabled && !!userId,
  });
}

export function useConvertAppUsage(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas['ConvertUsageRequest']) =>
      unwrap(api.POST('/api/v1/workspaces/{workspaceId}/activity/app-usage/convert', { ...wsPath(ws), body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', ws] }),
  });
}
